import { createTranslation } from '@choffmeister/i18next'
import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import React from 'react'

import { videoGalleryBreakPoint } from '../constants/breakPoints'
import { breakpoints } from '../constants/breakPoints'
import { GalleryVideoItemProps, homepageVideos } from '../constants/galleryVideos'
import { isDesktop, isMobile } from '../helpers/common'
import { isElementInView } from '../helpers/isElementInView'
import { useLanguage } from '../hooks/useLanguage'
import { useTranslation } from '../lang/index'
import Grid from './Grid'
import Image from './Image'
import { Link } from './Link'
import ProgressBar from './ProgressBar'
import Video from './Video'

interface VideoGalleryProps {
  heading?: string
  smallHeading?: string
  columnsTemplate?: string
  contentArray?: GalleryVideoItemProps[]
  removeBorderBlockFromVideos?: boolean
  breakPoint?: number
  cssStyles?: SerializedStyles
}

const VideoGallery: React.FunctionComponent<VideoGalleryProps> = ({
  heading,
  smallHeading,
  columnsTemplate,
  contentArray = homepageVideos,
  removeBorderBlockFromVideos = false,
  breakPoint = videoGalleryBreakPoint,
  cssStyles,
}) => {
  const [selectedBenefit, setSelectedBenefit] = React.useState(contentArray[0])
  const [loopThroughBenefits, setLoopThroughBenefits] = React.useState(true)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [inView, setInView] = React.useState(false)

  const { t } = useTranslation()

  React.useEffect(() => {
    const scrollHandler = () => {
      if (!inView && containerRef.current && isElementInView(containerRef.current)) {
        setInView(true)
      }
    }

    document.addEventListener('scroll', scrollHandler)

    return () => {
      document.removeEventListener('scroll', scrollHandler)
    }
  }, [inView])

  React.useEffect(() => {
    const tab =
      contentArray &&
      loopThroughBenefits &&
      inView &&
      setInterval(() => {
        setSelectedBenefit(contentArray[(contentArray.indexOf(selectedBenefit) + 1) % contentArray.length])
      }, selectedBenefit.duration * 1000)

    return () => (tab ? clearInterval(tab) : undefined)
  }, [selectedBenefit, loopThroughBenefits, inView])

  return (
    <div ref={containerRef} css={cssStyles}>
      {smallHeading ? (
        <h3 className="heading3 center" css={smallHeadingStyle}>
          {parse(smallHeading)}
        </h3>
      ) : (
        <h2 className="heading2 center center-on-small">{heading ? parse(heading) : t('VideoGallery.heading')}</h2>
      )}

      <Grid
        className="video-gallery-block"
        columns={2}
        columnsTemplate={columnsTemplate || '1fr 3fr'}
        columnGap={5}
        breakPoint={breakPoint}
        css={blockStyle(breakPoint)}
      >
        <div>
          {contentArray.map((benefit) => {
            const isCurrentBenefit = selectedBenefit === benefit
            const {
              title,
              description,
              linkText,
              linkURL,
              imgSrc,
              withoutBorder,
              mp4VideoSrc,
              webmVideoSrc,
              videoRatio,
              duration,
            } = benefit
            const { useTranslation } = createTranslation({
              title,
              description,
              linkText,
              linkURL,
            })
            const { t } = useTranslation()

            return (
              <div
                key={t('title')}
                className={!isCurrentBenefit ? 'opacity-on-hover' : undefined}
                css={sidemenuItemStyle(isCurrentBenefit, breakPoint)}
                onClick={() => {
                  setSelectedBenefit(benefit)
                  setLoopThroughBenefits(false)
                }}
              >
                {isMobile(breakPoint) && !isMobile() ? (
                  <Grid
                    className={isCurrentBenefit ? 'video-gallery-block-mobile' : ''}
                    columnsTemplate={isCurrentBenefit ? '1fr 2fr' : '1fr'}
                    columnGap={2}
                  >
                    <div>
                      <h3
                        className={`strong-body-1 ${isCurrentBenefit ? 'blue-text' : 'center'}`}
                        css={titleStyle(isCurrentBenefit)}
                      >
                        {parse(t('title'))}
                      </h3>
                      {description && (
                        <p className="body-1" css={descriptionStyle(isCurrentBenefit)}>
                          {parse(t('description'))}
                        </p>
                      )}
                      {isCurrentBenefit && (
                        <React.Fragment>
                          {t('linkText') !== 'linkText' && linkURL && (
                            <Link className="body-1" href={t('linkURL')} withArrow>
                              {t('linkText')}
                            </Link>
                          )}
                          <ProgressBar
                            cssStyles={progressBarStyle(breakPoint)}
                            height="2px"
                            fillColor="#0a1c33"
                            isCurrent={inView && isCurrentBenefit}
                            tabDuration={duration}
                            infiniteLoop={!loopThroughBenefits}
                          />
                        </React.Fragment>
                      )}
                    </div>
                    {isCurrentBenefit &&
                      (imgSrc ? (
                        <Image src={imgSrc} alt={t('title')} />
                      ) : removeBorderBlockFromVideos || withoutBorder ? (
                        <Video
                          key={mp4VideoSrc}
                          srcType={[
                            { src: mp4VideoSrc, type: 'video/mp4' },
                            { src: webmVideoSrc, type: 'video/webm' },
                          ]}
                          ratio={videoRatio}
                          playInView={!inView}
                          loop={loopThroughBenefits ? false : true}
                          loopDelay={2000}
                        />
                      ) : (
                        <div className="gray-bordered-block" css={mobileVideoStyle}>
                          <Video
                            key={mp4VideoSrc}
                            srcType={[
                              { src: mp4VideoSrc, type: 'video/mp4' },
                              { src: webmVideoSrc, type: 'video/webm' },
                            ]}
                            ratio={videoRatio}
                            playInView={!inView}
                            loop={loopThroughBenefits ? false : true}
                            loopDelay={2000}
                          />
                        </div>
                      ))}
                  </Grid>
                ) : (
                  <>
                    <h3
                      className={`strong-body-1 ${isMobile() && 'center'} ${isCurrentBenefit && 'blue-text'}`}
                      css={titleStyle(isCurrentBenefit)}
                    >
                      {parse(t('title'))}
                    </h3>
                    <p className={`body-1 ${isMobile() && 'center'}`} css={descriptionStyle(isCurrentBenefit)}>
                      {parse(t('description'))}
                    </p>
                    {isCurrentBenefit && (
                      <React.Fragment>
                        {t('linkText') !== 'linkText' &&
                          linkURL &&
                          (isMobile() ? (
                            <div className="center">
                              <Link className="body-1" href={t('linkURL')} withArrow>
                                {t('linkText')}
                              </Link>
                            </div>
                          ) : (
                            <Link className="body-1" href={t('linkURL')} withArrow>
                              {t('linkText')}
                            </Link>
                          ))}
                        <ProgressBar
                          cssStyles={progressBarStyle(breakPoint)}
                          height="2px"
                          fillColor="#0a1c33"
                          isCurrent={inView && isCurrentBenefit}
                          tabDuration={duration}
                          infiniteLoop={!loopThroughBenefits}
                        />
                        {!isDesktop(breakPoint + 1) &&
                          (imgSrc ? (
                            <Image src={imgSrc} alt={t('title')} />
                          ) : removeBorderBlockFromVideos || withoutBorder ? (
                            <Video
                              key={mp4VideoSrc}
                              srcType={[
                                { src: mp4VideoSrc, type: 'video/mp4' },
                                { src: webmVideoSrc, type: 'video/webm' },
                              ]}
                              ratio={videoRatio}
                              playInView={!inView}
                              loop={loopThroughBenefits ? false : true}
                              loopDelay={2000}
                            />
                          ) : (
                            <div className="gray-bordered-block" css={mobileVideoStyle}>
                              <Video
                                key={mp4VideoSrc}
                                srcType={[
                                  { src: mp4VideoSrc, type: 'video/mp4' },
                                  { src: webmVideoSrc, type: 'video/webm' },
                                ]}
                                ratio={videoRatio}
                                playInView={!inView}
                                loop={loopThroughBenefits ? false : true}
                                loopDelay={2000}
                              />
                            </div>
                          ))}
                      </React.Fragment>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
        {isDesktop(breakPoint + 1) &&
          (selectedBenefit.imgSrc ? (
            <Image src={selectedBenefit.imgSrc} alt={t('title')} />
          ) : removeBorderBlockFromVideos || selectedBenefit.withoutBorder ? (
            <Video
              key={selectedBenefit.mp4VideoSrc}
              loop={loopThroughBenefits ? false : true}
              srcType={[
                { src: selectedBenefit.mp4VideoSrc, type: 'video/mp4' },
                { src: selectedBenefit.webmVideoSrc, type: 'video/webm' },
              ]}
              ratio={selectedBenefit.videoRatio}
              playInView={!inView}
              loopDelay={2000}
            />
          ) : (
            <div className="gray-bordered-block">
              <Video
                key={selectedBenefit.mp4VideoSrc}
                loop={loopThroughBenefits ? false : true}
                srcType={[
                  { src: selectedBenefit.mp4VideoSrc, type: 'video/mp4' },
                  { src: selectedBenefit.webmVideoSrc, type: 'video/webm' },
                ]}
                ratio={selectedBenefit.videoRatio}
                playInView={!inView}
                loopDelay={2000}
              />
            </div>
          ))}
      </Grid>
    </div>
  )
}

export default VideoGallery

const blockStyle = (breakPoint: number) => css`
  @media screen and (min-width: ${breakPoint + 1}px) and (max-width: ${useLanguage() === 'en'
      ? breakpoints.large
      : breakpoints.xlarge - 1}px) {
    grid-template-columns: 1fr 2.5fr;
    column-gap: 3.5rem;
  }
`

const smallHeadingStyle = css`
  margin-bottom: 2rem;
`

const sidemenuItemStyle = (isCurrentBenefit: boolean, breakPoint: number) => css`
  padding: 1.5rem 0 1.5rem 0.5rem;
  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid #efeff0;
  }

  @media screen and (max-width: ${breakPoint}px) {
    max-width: 50rem;
    margin: auto;
    ${isCurrentBenefit ? `padding: 1rem 0` : 'padding-left: 0'};
  }
`

const titleStyle = (isCurrentBenefit: boolean) => css`
  margin-bottom: 0.25rem;

  .new {
    padding: 0 0.5rem;
    margin-left: 0.5rem;
    transform: translateY(-2px);
    color: ${isCurrentBenefit ? '#44505f' : '#318eff'};
    background-color: rgb(236, 243, 251);
    font-size: 0.65rem;
    border-radius: 0.25rem;
    display: inline-flex;
  }
`

const descriptionStyle = (isCurrentBenefit: boolean) => css`
  display: ${isCurrentBenefit ? 'block' : 'none'};
  margin-bottom: 0.25rem !important;
`

const progressBarStyle = (breakPoint: number) => css`
  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakPoint}px) {
    max-width: 50%;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin: 0.75rem auto 1.5rem;
  }
`

const mobileVideoStyle = css`
  margin-top: 1rem;
`
